import React, { useEffect, useState } from 'react'
import { Box, Button, TextField, Typography, useTheme } from '@mui/material'

type VerificationCodeInputProps = {
  type: 'numeric' | 'alphanumeric'
  placeholder: string
  isInvalid?: boolean
  onGetNewCode?: () => void
  onChange: (value: string) => void
  isLoading?: boolean
}

const formattedValue = (value: string, hyphenIndex: number | null): string => {
  if (!hyphenIndex) return value

  return `${value.substring(0, hyphenIndex)}${
    value.length > hyphenIndex ? `-${value.substring(hyphenIndex)}` : ''
  }`
}

const VerificationCodeInput: React.FC<VerificationCodeInputProps> = ({
  type,
  placeholder,
  isInvalid,
  onGetNewCode,
  onChange,
  isLoading
}) => {
  const theme = useTheme()
  const [value, setValue] = useState<string>('')

  const length = placeholder.replace('-', '').length
  const hyphenIndex =
    placeholder.indexOf('-') > 0 ? placeholder.indexOf('-') : null
  const patternForType = {
    numeric: '^[0-9]+$',
    alphanumeric: '^[a-zA-Z0-9]+$'
  }
  const matcher = new RegExp(patternForType[type])

  useEffect(() => onChange(value), [value])

  const onPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault()

    const pastedVal = e.clipboardData?.getData('text')
    if (!pastedVal || !matcher.test(pastedVal)) return

    setValue(pastedVal.substring(0, length))
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const input = e.target.value.replace('-', '')
    const isValid = matcher.test(input) || input === ''

    if (isValid) setValue(input)
  }

  return (
    <Box
      sx={{
        marginBottom: theme.spacing(2)
      }}
    >
      <TextField
        id="verification-code"
        required
        fullWidth
        variant="outlined"
        placeholder={placeholder}
        value={formattedValue(value, hyphenIndex)}
        helperText={
          isInvalid
            ? 'The code you entered was incorrect. Please try again.'
            : ''
        }
        error={isInvalid}
        FormHelperTextProps={{
          style: {
            margin: 0
          }
        }}
        inputProps={{
          style: {
            fill: '#DCF6FF',
            background: 'rgb(220, 246, 255)',
            textAlign: 'center',
            letterSpacing: '.5em',
            color: '#000000'
          },
          maxLength: hyphenIndex ? length + 1 : length
        }}
        onChange={handleChange}
        onPaste={onPaste}
      />
      {onGetNewCode && (
        <Typography component="div" variant="body1" gutterBottom sx={{ mt: 2 }}>
          Didn't get the code?
          <Button
            style={{
              textTransform: 'none',
              letterSpacing: 0,
              color: '#0066FF',
              fontSize: '1rem',
              backgroundColor: 'transparent'
            }}
            variant="text"
            size="small"
            onClick={onGetNewCode}
            disabled={isLoading}
          >
            Get a new code.
          </Button>
        </Typography>
      )}
    </Box>
  )
}

export default VerificationCodeInput
